import React, { useEffect, useMemo, useState, createContext, useContext } from 'react';
import { APARTMENTS } from '../pages/cm/appartamentiData';
// ---------------------------------------------------------------------------
// Single source of truth for the connected platform: cleaners (team) + jobs
// (incarichi). Actions here ripple across every screen that reads this context
// (Incarichi, Team, Guadagni, Qualità, dashboard rollups).
// ---------------------------------------------------------------------------
export type Availability = 'disponibile' | 'occupato' | 'off';
export type ContactChannel = 'whatsapp' | 'telefono' | 'email' | 'sms';
export interface Cleaner {
  id: string;
  name: string;
  zones: string[];
  availability: Availability;
  phone?: string;
  email?: string;
  channels?: ContactChannel[];
}
export type JobStatus = 'da_assegnare' | 'assegnata' | 'in_corso' | 'completata';
// --- Assignment confirmation lifecycle (technically realistic) -------------
// Only states we can actually record: we SEND the request (app action) and the
// collaborator RESPONDS (explicit in-app action). No delivered/read receipts.
export type AssignmentPhase = 'inviata' | 'confermata' | 'rifiutata';
export type InteractionType = 'inviata' | 'sollecito_auto' | 'sollecito_manuale' | 'confermata' | 'rifiutata';
export interface InteractionEvent {
  id: string;
  type: InteractionType;
  channel: ContactChannel;
  at: number;
}
export interface AssignmentState {
  cleanerId: string;
  cleanerName: string;
  channel: ContactChannel;
  phase: AssignmentPhase;
  sentAt: number;
  respondedAt?: number;
  lastReminderAt?: number;
  reminderCount: number;
  events: InteractionEvent[];
}
export interface Job {
  id: string;
  apartmentId: string;
  apartmentName: string;
  zone: string;
  type: string;
  date: string;
  time: string;
  cleanerId: string | null;
  cleanerName: string | null;
  status: JobStatus;
  earning: number;
  score?: number | null;
  assignment?: AssignmentState;
}
// Soglia (ms) oltre la quale parte un sollecito automatico simulato.
// Nel prototipo è breve (rappresenta ore nella realtà).
const REMINDER_THRESHOLD_MS = 25000;
function uid(prefix: string): string {
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
}
// Canale preferito di un cleaner (WhatsApp se disponibile, poi telefono/sms/email).
function preferredChannel(channels?: ContactChannel[]): ContactChannel {
  if (!channels || channels.length === 0) return 'whatsapp';
  const order: ContactChannel[] = ['whatsapp', 'telefono', 'sms', 'email'];
  return order.find(c => channels.includes(c)) ?? channels[0];
}
export function initials(name: string): string {
  return name.split(' ').map(p => p[0]).filter(Boolean).slice(0, 2).join('');
}
// Short zone label, e.g. "Milano · Navigli" -> "Navigli"
function shortZone(zone: string): string {
  const parts = zone.split('·');
  return (parts[parts.length - 1] || zone).trim();
}
const INITIAL_CLEANERS: Cleaner[] = [{
  id: 'cl1',
  name: 'Francesca N.',
  zones: ['Navigli', 'Tortona'],
  availability: 'disponibile',
  phone: '+39 348 552 1180',
  email: 'francesca.n@cleanai.it',
  channels: ['whatsapp', 'telefono', 'email']
}, {
  id: 'cl2',
  name: 'Luca B.',
  zones: ['Porta Romana', 'Tortona', 'Centro'],
  availability: 'occupato',
  phone: '+39 347 119 8842',
  email: 'luca.b@cleanai.it',
  channels: ['whatsapp', 'telefono']
}, {
  id: 'cl3',
  name: 'Sara M.',
  zones: ['Isola', 'Centro'],
  availability: 'disponibile',
  phone: '+39 333 904 2271',
  email: 'sara.m@cleanai.it',
  channels: ['telefono', 'sms', 'email']
}, {
  id: 'cl4',
  name: 'Giulia R.',
  zones: ['Navigli', 'Isola'],
  availability: 'off',
  phone: '+39 320 661 7793',
  channels: ['whatsapp']
}];
function cleanerIdByName(name: string): string | null {
  const c = INITIAL_CLEANERS.find(cl => cl.name === name);
  return c ? c.id : null;
}
const INITIAL_JOBS: Job[] = [{
  id: 'j1',
  apartmentId: 'a1',
  apartmentName: 'Loft Navigli',
  zone: 'Navigli',
  type: 'Pulizia turnover',
  date: 'Oggi',
  time: '09:30',
  cleanerId: cleanerIdByName('Francesca N.'),
  cleanerName: 'Francesca N.',
  status: 'in_corso',
  earning: 45
}, {
  id: 'j2',
  apartmentId: 'a2',
  apartmentName: 'Bilo Porta Romana',
  zone: 'Porta Romana',
  type: 'Pulizia turnover',
  date: 'Oggi',
  time: '11:00',
  cleanerId: cleanerIdByName('Luca B.'),
  cleanerName: 'Luca B.',
  status: 'assegnata',
  earning: 42
}, {
  id: 'j3',
  apartmentId: 'a3',
  apartmentName: 'Studio Isola',
  zone: 'Isola',
  type: 'Pulizia turnover',
  date: 'Oggi',
  time: '14:00',
  cleanerId: 'cl3',
  cleanerName: 'Sara M.',
  status: 'da_assegnare',
  earning: 40,
  assignment: {
    cleanerId: 'cl3',
    cleanerName: 'Sara M.',
    channel: 'whatsapp',
    phase: 'inviata',
    sentAt: Date.now() - 30000,
    reminderCount: 0,
    events: [{
      id: 'ev-seed-j3-1',
      type: 'inviata',
      channel: 'whatsapp',
      at: Date.now() - 30000
    }]
  }
}, {
  id: 'j6',
  apartmentId: 'a2',
  apartmentName: 'Bilo Porta Romana',
  zone: 'Porta Romana',
  type: 'Deep cleaning',
  date: 'Domani',
  time: '09:00',
  cleanerId: null,
  cleanerName: null,
  status: 'da_assegnare',
  earning: 70
}, {
  id: 'j4',
  apartmentId: 'a4',
  apartmentName: 'Trilo Centro',
  zone: 'Centro',
  type: 'Deep cleaning',
  date: 'Ieri',
  time: '10:00',
  cleanerId: cleanerIdByName('Sara M.'),
  cleanerName: 'Sara M.',
  status: 'completata',
  earning: 80,
  score: 94
}, {
  id: 'j5',
  apartmentId: 'a1',
  apartmentName: 'Loft Navigli',
  zone: 'Navigli',
  type: 'Pulizia turnover',
  date: 'Ieri',
  time: '15:00',
  cleanerId: cleanerIdByName('Francesca N.'),
  cleanerName: 'Francesca N.',
  status: 'completata',
  earning: 45,
  score: 90
}];
interface PlatformContextValue {
  cleaners: Cleaner[];
  jobs: Job[];
  addCleaner: (c: Omit<Cleaner, 'id'>) => void;
  assignCleaner: (jobId: string, cleanerId: string) => void;
  // Assignment confirmation lifecycle
  sendAssignment: (jobId: string, cleanerId: string, channel?: ContactChannel) => void;
  confirmAssignment: (jobId: string) => void;
  rejectAssignment: (jobId: string) => void;
  sendReminder: (jobId: string, kind?: 'auto' | 'manuale') => void;
  startJob: (jobId: string) => void;
  completeJob: (jobId: string, score?: number) => void;
  // Generate planned cleaning jobs when a contract is formalized.
  generateContractJobs: (input: {
    apartmentId?: string;
    apartmentName?: string;
    zone: string;
    count: number;
    type?: string;
    earning: number;
  }) => number;
  // Derived helpers
  cleanerById: (id: string | null | undefined) => Cleaner | undefined;
  jobsForCleaner: (cleanerId: string) => Job[];
  jobsForApartment: (apartmentId: string) => Job[];
  cleanerAvgScore: (cleanerName: string) => number | null;
  activeLoad: (cleanerId: string) => number;
  // Assignment helpers
  pendingAssignments: () => Job[];
  isAwaitingResponse: (jobId: string) => boolean;
  assignmentAge: (jobId: string) => number | null;
}
const PlatformContext = createContext<PlatformContextValue | undefined>(undefined);
export function PlatformProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [cleaners, setCleaners] = useState<Cleaner[]>(INITIAL_CLEANERS);
  const [jobs, setJobs] = useState<Job[]>(INITIAL_JOBS);
  const addCleaner = (c: Omit<Cleaner, 'id'>) => {
    setCleaners(prev => [...prev, {
      ...c,
      id: `cl-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`
    }]);
  };
  const assignCleaner = (jobId: string, cleanerId: string) => {
    setJobs(prev => prev.map(j => j.id === jobId ? {
      ...j,
      cleanerId,
      cleanerName: cleaners.find(c => c.id === cleanerId)?.name ?? null,
      status: j.status === 'da_assegnare' ? 'assegnata' : j.status
    } : j));
  };
  // Send an assignment request to a cleaner. The job stays 'da_assegnare'
  // (logically "in attesa") with an assignment in phase 'inviata' until the
  // cleaner responds. Records an 'inviata' interaction event.
  const sendAssignment = (jobId: string, cleanerId: string, channel?: ContactChannel) => {
    const cleaner = cleaners.find(c => c.id === cleanerId);
    const ch = channel ?? preferredChannel(cleaner?.channels);
    const now = Date.now();
    setJobs(prev => prev.map(j => j.id === jobId ? {
      ...j,
      cleanerId,
      cleanerName: cleaner?.name ?? null,
      status: 'da_assegnare',
      assignment: {
        cleanerId,
        cleanerName: cleaner?.name ?? '',
        channel: ch,
        phase: 'inviata',
        sentAt: now,
        reminderCount: 0,
        events: [{
          id: uid('ev'),
          type: 'inviata',
          channel: ch,
          at: now
        }]
      }
    } : j));
  };
  const confirmAssignment = (jobId: string) => {
    const now = Date.now();
    setJobs(prev => prev.map(j => {
      if (j.id !== jobId || !j.assignment) return j;
      return {
        ...j,
        status: 'assegnata',
        assignment: {
          ...j.assignment,
          phase: 'confermata',
          respondedAt: now,
          events: [...j.assignment.events, {
            id: uid('ev'),
            type: 'confermata',
            channel: j.assignment.channel,
            at: now
          }]
        }
      };
    }));
  };
  const rejectAssignment = (jobId: string) => {
    const now = Date.now();
    setJobs(prev => prev.map(j => {
      if (j.id !== jobId || !j.assignment) return j;
      return {
        ...j,
        // Torna disponibile per la riassegnazione.
        status: 'da_assegnare',
        cleanerId: null,
        cleanerName: null,
        assignment: {
          ...j.assignment,
          phase: 'rifiutata',
          respondedAt: now,
          events: [...j.assignment.events, {
            id: uid('ev'),
            type: 'rifiutata',
            channel: j.assignment.channel,
            at: now
          }]
        }
      };
    }));
  };
  const sendReminder = (jobId: string, kind: 'auto' | 'manuale' = 'manuale') => {
    const now = Date.now();
    setJobs(prev => prev.map(j => {
      if (j.id !== jobId || !j.assignment || j.assignment.phase !== 'inviata') return j;
      return {
        ...j,
        assignment: {
          ...j.assignment,
          lastReminderAt: now,
          reminderCount: j.assignment.reminderCount + 1,
          events: [...j.assignment.events, {
            id: uid('ev'),
            type: kind === 'auto' ? 'sollecito_auto' : 'sollecito_manuale',
            channel: j.assignment.channel,
            at: now
          }]
        }
      };
    }));
  };
  const startJob = (jobId: string) => {
    setJobs(prev => prev.map(j => j.id === jobId ? {
      ...j,
      status: 'in_corso'
    } : j));
  };
  const completeJob = (jobId: string, score?: number) => {
    setJobs(prev => prev.map(j => j.id === jobId ? {
      ...j,
      status: 'completata',
      score: score ?? j.score ?? null
    } : j));
  };
  const generateContractJobs: PlatformContextValue['generateContractJobs'] = ({
    apartmentId,
    apartmentName,
    zone,
    count,
    type = 'Pulizia turnover',
    earning
  }) => {
    const z = shortZone(zone);
    const resolvedApt = apartmentId && APARTMENTS.find(a => a.id === apartmentId) || APARTMENTS.find(a => a.name === apartmentName) || APARTMENTS.find(a => shortZone(a.zone) === z);
    const aptId = resolvedApt?.id ?? apartmentId ?? `apt-${z}`;
    const aptName = resolvedApt?.name ?? apartmentName ?? `Appartamento ${z}`;
    const newJobs: Job[] = Array.from({
      length: count
    }).map((_, i) => ({
      id: `j-${Date.now()}-${i}-${Math.random().toString(36).slice(2, 5)}`,
      apartmentId: aptId,
      apartmentName: aptName,
      zone: z,
      type,
      date: 'Da pianificare',
      time: '—',
      cleanerId: null,
      cleanerName: null,
      status: 'da_assegnare',
      earning
    }));
    setJobs(prev => [...newJobs, ...prev]);
    return count;
  };
  const cleanerById = (id: string | null | undefined) => id ? cleaners.find(c => c.id === id) : undefined;
  const jobsForCleaner = (cleanerId: string) => jobs.filter(j => j.cleanerId === cleanerId);
  const jobsForApartment = (apartmentId: string) => jobs.filter(j => j.apartmentId === apartmentId);
  const activeLoad = (cleanerId: string) => jobs.filter(j => j.cleanerId === cleanerId && (j.status === 'assegnata' || j.status === 'in_corso')).length;
  // Average CleanAI score from the static interventions history per cleaner.
  const scoreIndex = useMemo(() => {
    const map: Record<string, number[]> = {};
    APARTMENTS.forEach(a => a.interventions.forEach(i => {
      if (i.score != null) {
        map[i.cleaner] = map[i.cleaner] || [];
        map[i.cleaner].push(i.score);
      }
    }));
    // also fold in completed jobs that carry a score
    jobs.forEach(j => {
      if (j.status === 'completata' && j.score != null && j.cleanerName) {
        map[j.cleanerName] = map[j.cleanerName] || [];
      }
    });
    return map;
  }, [jobs]);
  const cleanerAvgScore = (cleanerName: string): number | null => {
    const arr = scoreIndex[cleanerName];
    if (!arr || arr.length === 0) return null;
    return Math.round(arr.reduce((s, n) => s + n, 0) / arr.length);
  };
  // --- Assignment-related derived helpers ----------------------------------
  const pendingAssignments = () => jobs.filter(j => j.assignment?.phase === 'inviata');
  const isAwaitingResponse = (jobId: string) => {
    const j = jobs.find(x => x.id === jobId);
    return j?.assignment?.phase === 'inviata';
  };
  const assignmentAge = (jobId: string): number | null => {
    const j = jobs.find(x => x.id === jobId);
    if (!j?.assignment) return null;
    return Date.now() - j.assignment.sentAt;
  };
  // Auto-reminder timer: simulated. When a pending assignment has been waiting
  // longer than the threshold since the last contact, fire a 'sollecito_auto'.
  useEffect(() => {
    const interval = setInterval(() => {
      const now = Date.now();
      setJobs(prev => prev.map(j => {
        if (!j.assignment || j.assignment.phase !== 'inviata') return j;
        const lastContact = j.assignment.lastReminderAt ?? j.assignment.sentAt;
        if (now - lastContact < REMINDER_THRESHOLD_MS) return j;
        return {
          ...j,
          assignment: {
            ...j.assignment,
            lastReminderAt: now,
            reminderCount: j.assignment.reminderCount + 1,
            events: [...j.assignment.events, {
              id: uid('ev'),
              type: 'sollecito_auto',
              channel: j.assignment.channel,
              at: now
            }]
          }
        };
      }));
    }, 5000);
    return () => clearInterval(interval);
  }, []);
  return <PlatformContext.Provider value={{
    cleaners,
    jobs,
    addCleaner,
    assignCleaner,
    sendAssignment,
    confirmAssignment,
    rejectAssignment,
    sendReminder,
    startJob,
    completeJob,
    generateContractJobs,
    cleanerById,
    jobsForCleaner,
    jobsForApartment,
    cleanerAvgScore,
    activeLoad,
    pendingAssignments,
    isAwaitingResponse,
    assignmentAge
  }} data-id="element-1184">
      {children}
    </PlatformContext.Provider>;
}
export function usePlatform(): PlatformContextValue {
  const ctx = useContext(PlatformContext);
  if (!ctx) {
    throw new Error('usePlatform must be used within a PlatformProvider');
  }
  return ctx;
}